
import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, doc, getDocs, getDoc, updateDoc, deleteDoc, Timestamp, setDoc, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Select from 'react-select';
import { useAuth } from './contexts/AuthContext';

function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const { user } = useAuth();
  const [, setSignupRefreshTrigger] = useState(0);

  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    playersPerCourt: 4,
    location: ''
  });

  const [scheduleForm, setScheduleForm] = useState({
    date: '',
    time: '19:00',
    maxPlayers: 8,
    description: ''
  });

  const [showInviteUser, setShowInviteUser] = useState(false);
  const [selectedUserToInvite, setSelectedUserToInvite] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  const auth = getAuth();
  const db = getFirestore();

  const fetchAllUsers = useCallback(async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [db]);

  const acceptInvite = async (invite) => {
    if (!user) return;

    try {
      // Get the user's actual name from their user profile
      const userProfileRef = doc(db, 'userProfiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      const userName = userProfileSnap.exists()
        ? userProfileSnap.data().name
        : user.displayName || user.email || 'Anonymous';

      // Add user to the group's members subcollection
      const memberRef = doc(db, 'groups', invite.groupId, 'members', user.uid);
      const memberData = {
        userId: user.uid,
        userName: userName,
        joinedAt: Timestamp.now(),
        role: 'member'
      };

      await setDoc(memberRef, memberData);

      // Add the group to the user's groups subcollection
      const userGroupRef = doc(db, 'userProfiles', user.uid, 'groups', invite.groupId);
      const userGroupData = {
        groupId: invite.groupId,
        groupName: invite.groupName,
        joinedAt: Timestamp.now(),
        role: 'member'
      };

      await setDoc(userGroupRef, userGroupData);

      // Update the invite status to accepted
      const userInviteRef = doc(db, 'userProfiles', user.uid, 'invites', invite.groupId);
      await updateDoc(userInviteRef, { status: 'accepted' });

      // Refresh the data
      fetchUserGroups(user.uid);
      fetchUserInvites(user.uid);

      alert(`You've joined ${invite.groupName}!`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert('Failed to accept invitation: ' + error.message);
    }
  };

  const declineInvite = async (invite) => {
    if (!user) return;

    try {
      // Update the invite status to declined
      const userInviteRef = doc(db, 'userProfiles', user.uid, 'invites', invite.groupId);
      await updateDoc(userInviteRef, { status: 'declined' });

      // Refresh the invites
      fetchUserInvites(user.uid);

      alert(`You've declined the invitation to ${invite.groupName}`);
    } catch (error) {
      console.error('Error declining invite:', error);
      alert('Failed to decline invitation: ' + error.message);
    }
  };

  const fetchUserInvites = useCallback(async (userId) => {
    try {
      const userInvitesSnapshot = await getDocs(collection(db, 'userProfiles', userId, 'invites'));
      const invitesData = userInvitesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingInvites(invitesData);
    } catch (error) {
      console.error('Error fetching user invites:', error);
    }
  }, [db]);

  const fetchUserGroups = useCallback(async (userId) => {
    setLoading(true);
    try {
      // Get user's groups from their userProfiles subcollection
      const userGroupsSnapshot = await getDocs(collection(db, 'userProfiles', userId, 'groups'));

      const groupsData = [];

      for (const userGroupDoc of userGroupsSnapshot.docs) {
        const userGroupData = userGroupDoc.data();

        // Get the full group details
        const groupRef = doc(db, 'groups', userGroupData.groupId);
        const groupSnap = await getDoc(groupRef);

        if (groupSnap.exists()) {
          const groupData = groupSnap.data();
          groupsData.push({
            id: userGroupData.groupId,
            ...groupData,
            userRole: userGroupData.role
          });
        }
      }

      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    if (user) {
      fetchUserGroups(user.uid);
      fetchUserInvites(user.uid);
      fetchAllUsers();
    } else {
      setGroups([]);
      setAllUsers([]);
      setPendingInvites([]);
    }
  }, [user, fetchUserGroups, fetchUserInvites, fetchAllUsers]);

  const createGroup = async () => {
    if (!groupForm.name.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (!user) {
      alert('You must be logged in to create a group');
      return;
    }

    try {
      // Get the user's actual name from their user profile
      const userProfileRef = doc(db, 'userProfiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      const userName = userProfileSnap.exists()
        ? userProfileSnap.data().name
        : user.displayName || user.email || 'Anonymous';

      // Create the group document
      const groupRef = doc(collection(db, 'groups'));
      const groupData = {
        name: groupForm.name,
        description: groupForm.description,
        playersPerCourt: groupForm.playersPerCourt,
        location: groupForm.location,
        createdBy: user.uid,
        createdByName: userName,
        createdAt: Timestamp.now()
      };

      await setDoc(groupRef, groupData);

      // Add creator as a member
      const memberRef = doc(db, 'groups', groupRef.id, 'members', user.uid);
      const memberData = {
        userId: user.uid,
        userName: userName,
        joinedAt: Timestamp.now(),
        role: 'admin'
      };

      await setDoc(memberRef, memberData);

      // Add the group to the user's groups subcollection
      const userGroupRef = doc(db, 'userProfiles', user.uid, 'groups', groupRef.id);
      const userGroupData = {
        groupId: groupRef.id,
        groupName: groupForm.name,
        joinedAt: Timestamp.now(),
        role: 'admin'
      };

      await setDoc(userGroupRef, userGroupData);

      // Reset form and refresh groups
      setGroupForm({ name: '', description: '', playersPerCourt: 4, location: '' });
      setShowCreateGroup(false);
      fetchUserGroups(user.uid);

      alert('Group created successfully!');
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group: ' + error.message);
    }
  };

  const addSchedule = async (e) => {
    e.preventDefault();

    if (!scheduleForm.date || !scheduleForm.time) {
      alert('Please fill in all required fields');
      return;
    }

    if (!selectedGroup) {
      alert('Please select a group first');
      return;
    }

    try {
      const scheduleRef = doc(collection(db, 'groups', selectedGroup.id, 'schedules'));
      const scheduleData = {
        date: scheduleForm.date,
        time: scheduleForm.time,
        maxPlayers: scheduleForm.maxPlayers,
        description: scheduleForm.description,
        createdAt: Timestamp.now(),
        status: 'active'
      };

      await setDoc(scheduleRef, scheduleData);

      // Reset form and refresh sessions
      setScheduleForm({ date: '', time: '19:00', maxPlayers: 8, description: '' });
      setShowAddSchedule(false);
      fetchGroupSchedules(selectedGroup.id);

      alert('Schedule added successfully!');
    } catch (error) {
      console.error('Error adding schedule:', error);
      alert('Failed to add schedule: ' + error.message);
    }
  };

  const fetchGroupSchedules = async (groupId) => {
    try {
      const schedulesSnapshot = await getDocs(collection(db, 'groups', groupId, 'schedules'));
      const schedulesData = schedulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSessions(schedulesData);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const signUpForSession = async (scheduleId, schedule) => {
    if (!user || !selectedGroup) return;

    try {
      // Check if user is already signed up
      const existingSignupRef = doc(db, 'groups', selectedGroup.id, 'schedules', scheduleId, 'signups', user.uid);
      const existingSignup = await getDoc(existingSignupRef);

      if (existingSignup.exists()) {
        // User is already signed up, cancel signup
        await deleteDoc(existingSignupRef);

        // Check if we need to move someone from waitlist to confirmed
        await updateWaitlist(scheduleId, schedule);

        alert('Signup cancelled successfully!');
        setSignupRefreshTrigger(prev => prev + 1);
        return;
      }

      // Get current signups to determine status
      const signupsSnapshot = await getDocs(collection(db, 'groups', selectedGroup.id, 'schedules', scheduleId, 'signups'));
      const confirmedSignups = signupsSnapshot.docs.filter(doc => doc.data().status === 'confirmed');

      const status = confirmedSignups.length < schedule.maxPlayers ? 'confirmed' : 'waitlist';

      // Get the user's actual name from their user profile
      const userProfileRef = doc(db, 'userProfiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      const userName = userProfileSnap.exists()
        ? userProfileSnap.data().name
        : user.displayName || user.email || 'Anonymous';

      const signupData = {
        userId: user.uid,
        userName: userName,
        signedUpAt: Timestamp.now(),
        status
      };

      await setDoc(existingSignupRef, signupData);

      const statusMessage = status === 'confirmed' ? 'confirmed' : 'added to waitlist';
      alert(`Successfully ${statusMessage} for this session!`);

      // Trigger refresh of signups display
      setSignupRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error signing up for session:', error);
      alert('Failed to sign up: ' + error.message);
    }
  };

  const updateWaitlist = async (scheduleId, schedule) => {
    try {
      const signupsSnapshot = await getDocs(collection(db, 'groups', selectedGroup.id, 'schedules', scheduleId, 'signups'));
      const confirmedSignups = signupsSnapshot.docs.filter(doc => doc.data().status === 'confirmed');

      // If we have space, move first person from waitlist to confirmed
      if (confirmedSignups.length < schedule.maxPlayers) {
        const waitlistSignups = signupsSnapshot.docs
          .filter(doc => doc.data().status === 'waitlist')
          .sort((a, b) => a.data().signedUpAt.toMillis() - b.data().signedUpAt.toMillis());

        if (waitlistSignups.length > 0) {
          await updateDoc(waitlistSignups[0].ref, { status: 'confirmed' });
        }
      }
    } catch (error) {
      console.error('Error updating waitlist:', error);
    }
  };

  const inviteUserToGroup = async () => {
    if (!selectedUserToInvite || !selectedGroup) {
      alert('Please select a user and group');
      return;
    }

    try {
      // Get the inviter's actual name from their user profile
      const userProfileRef = doc(db, 'userProfiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      const inviterName = userProfileSnap.exists()
        ? userProfileSnap.data().name
        : user.displayName || user.email || 'Anonymous';

      // Create invite in the target user's invites subcollection
      const inviteRef = doc(db, 'userProfiles', selectedUserToInvite.id, 'invites', selectedGroup.id);
      const inviteData = {
        groupId: selectedGroup.id,
        groupName: selectedGroup.name,
        invitedBy: user.uid,
        invitedByName: inviterName,
        invitedAt: Timestamp.now(),
        status: 'pending'
      };

      await setDoc(inviteRef, inviteData);

      setSelectedUserToInvite(null);
      setShowInviteUser(false);

      alert(`Invitation sent to ${selectedUserToInvite.name}!`);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation: ' + error.message);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      const membersSnapshot = await getDocs(collection(db, 'groups', groupId, 'members'));
      const membersData = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroupMembers(membersData);
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!user) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to delete the group "${selectedGroup.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Get all group members first
      const membersSnapshot = await getDocs(collection(db, 'groups', groupId, 'members'));
      const members = membersSnapshot.docs.map(doc => doc.data());

      // Create a batch to handle all deletions
      const batch = writeBatch(db);

      // Delete the group document
      const groupRef = doc(db, 'groups', groupId);
      batch.delete(groupRef);

      // Remove group from all members' userProfiles
      for (const member of members) {
        const userGroupRef = doc(db, 'userProfiles', member.userId, 'groups', groupId);
        batch.delete(userGroupRef);

        // Also delete any pending invites for this group
        const userInviteRef = doc(db, 'userProfiles', member.userId, 'invites', groupId);
        batch.delete(userInviteRef);
      }

      // Delete all group schedules and their signups
      const schedulesSnapshot = await getDocs(collection(db, 'groups', groupId, 'schedules'));
      for (const scheduleDoc of schedulesSnapshot.docs) {
        // Delete all signups for this schedule
        const signupsSnapshot = await getDocs(collection(db, 'groups', groupId, 'schedules', scheduleDoc.id, 'signups'));
        for (const signupDoc of signupsSnapshot.docs) {
          batch.delete(signupDoc.ref);
        }
        // Delete the schedule
        batch.delete(scheduleDoc.ref);
      }

      // Delete all group members
      for (const memberDoc of membersSnapshot.docs) {
        batch.delete(memberDoc.ref);
      }

      // Commit all deletions
      await batch.commit();

      // Refresh the groups list
      fetchUserGroups(user.uid);

      // Go back to groups list
      setSelectedGroup(null);

      alert('Group deleted successfully!');
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Failed to delete group: ' + error.message);
    }
  };

  const openGroup = async (group) => {
    setSelectedGroup(group);
    setSelectedSession(null);
    await fetchGroupSchedules(group.id);
    await fetchGroupMembers(group.id);
  };

  const openSession = async (session) => {
    setSelectedSession(session);
  };

  const filterUserOption = (option, inputValue) => {
    const name = option.name || '';
    const email = option.email || '';
    return name.toLowerCase().includes(inputValue.toLowerCase()) ||
      email.toLowerCase().includes(inputValue.toLowerCase());
  };

  const formatUserOptionLabel = (option, { inputValue }) => {
    const name = option.name || 'No name';
    const email = option.email || 'No email';

    return (
      <div>
        <div style={{ fontWeight: 'bold' }}>{name}</div>
        <div style={{ fontSize: '0.8em', color: '#666' }}>{email}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Loading groups...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Groups</h1>
          <p className="text-gray-600">Create and manage your tennis groups</p>
        </div>

        {/* Pending Invites */}
        {pendingInvites.filter(invite => invite.status === 'pending').length > 0 && (
          <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-800 mb-4">Pending Invitations</h3>
            <div className="space-y-3">
              {pendingInvites.filter(invite => invite.status === 'pending').map(invite => (
                <div key={invite.id} className="flex items-center justify-between bg-white p-4 rounded-lg">
                  <div>
                    <div className="font-medium">{invite.groupName}</div>
                    <div className="text-sm text-gray-600">Invited by {invite.invitedByName}</div>
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => acceptInvite(invite)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => declineInvite(invite)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!selectedGroup ? (
          /* Groups List View */
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Your Groups</h2>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
              >
                Create Group
              </button>
            </div>

            {groups.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <div className="text-gray-500 mb-4">You haven't joined any groups yet</div>
                <button
                  onClick={() => setShowCreateGroup(true)}
                  className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                >
                  Create Your First Group
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groups.map(group => (
                  <div
                    key={group.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openGroup(group)}
                  >
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{group.name}</h3>
                    <p className="text-gray-600 mb-4">{group.description}</p>
                    <div className="text-sm text-gray-500">
                      <div>📍 {group.location}</div>
                      <div>👥 {group.playersPerCourt} players per court</div>
                      <div>🏆 Role: {group.userRole}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : !selectedSession ? (
          /* Group Detail View */
          <div>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setSelectedGroup(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                ← Back to Groups
              </button>
              <h2 className="text-2xl font-semibold text-gray-800">{selectedGroup.name}</h2>
              {selectedGroup.userRole === 'admin' && (
                <>
                  <button
                    onClick={() => setShowInviteUser(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Invite User
                  </button>
                  <button
                    onClick={() => deleteGroup(selectedGroup.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete Group
                  </button>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Sessions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">Scheduled Sessions</h3>
                  {selectedGroup.userRole === 'admin' && (
                    <button
                      onClick={() => setShowAddSchedule(true)}
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                    >
                      Add Session
                    </button>
                  )}
                </div>

                {sessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No sessions scheduled yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sessions.map(session => (
                      <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-gray-800">
                              {new Date(session.date).toLocaleDateString()} at {session.time}
                            </div>
                            {session.description && (
                              <div className="text-gray-600 text-sm">{session.description}</div>
                            )}
                            <div className="text-sm text-gray-500">
                              Max players: {session.maxPlayers}
                            </div>
                          </div>
                          <button
                            onClick={() => openSession(session)}
                            className="text-sky-600 hover:text-sky-800 text-sm"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Members */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Members</h3>
                <div className="space-y-3">
                  {groupMembers.map(member => (
                    <div key={member.id} className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{member.userName}</div>
                        <div className="text-sm text-gray-500">Role: {member.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Session Details View */
          <SessionDetails
            session={selectedSession}
            onSignUp={signUpForSession}
            selectedGroup={selectedGroup}
            onRefresh={() => setSignupRefreshTrigger(prev => prev + 1)}
            onBack={() => setSelectedSession(null)}
          />
        )}

        {/* Create Group Modal */}
        {showCreateGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold mb-4">Create New Group</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="Enter group name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    rows="3"
                    placeholder="Describe your group"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Players per Court
                  </label>
                  <select
                    value={groupForm.playersPerCourt}
                    onChange={(e) => setGroupForm({ ...groupForm, playersPerCourt: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  >
                    <option value={2}>2 (Singles)</option>
                    <option value={4}>4 (Doubles)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={groupForm.location}
                    onChange={(e) => setGroupForm({ ...groupForm, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="Where do you usually play?"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateGroup(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createGroup}
                  className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  Create Group
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Schedule Modal */}
        {showAddSchedule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold mb-4">Add Session</h3>
              <form onSubmit={addSchedule}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <input
                      type="time"
                      value={scheduleForm.time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Players
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="20"
                      value={scheduleForm.maxPlayers}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, maxPlayers: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={scheduleForm.description}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      rows="3"
                      placeholder="Optional session details"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddSchedule(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                  >
                    Add Session
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold mb-4">Invite User to {selectedGroup?.name}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select User
                  </label>
                  <Select
                    options={(() => {
                      const filteredUsers = allUsers.filter(u =>
                        u.id !== user?.uid &&
                        !groupMembers.some(member => member.userId === u.id)
                      );
                      console.log('Filtered users for select:', filteredUsers);
                      console.log('All users:', allUsers);
                      console.log('Group members:', groupMembers);
                      return filteredUsers;
                    })()}
                    getOptionLabel={(option) => option.name || option.email}
                    getOptionValue={(option) => option.id}
                    formatOptionLabel={formatUserOptionLabel}
                    filterOption={filterUserOption}
                    value={selectedUserToInvite}
                    onChange={setSelectedUserToInvite}
                    placeholder="Search for users..."
                    isClearable
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowInviteUser(false);
                    setSelectedUserToInvite(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={inviteUserToGroup}
                  disabled={!selectedUserToInvite}
                  className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Send Invite
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SessionDetails({ session, onSignUp, selectedGroup, onRefresh, onBack }) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [refreshTrigger] = useState(0);

  const auth = getAuth();
  const db = getFirestore();

  const fetchSignups = useCallback(async () => {
    if (!session || !selectedGroup) return;

    setLoading(true);
    try {
      const signupsSnapshot = await getDocs(collection(db, 'groups', selectedGroup.id, 'schedules', session.id, 'signups'));
      const signupsData = signupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by signup time
      signupsData.sort((a, b) => a.signedUpAt.toMillis() - b.signedUpAt.toMillis());

      setSignups(signupsData);
    } catch (error) {
      console.error('Error fetching signups:', error);
    } finally {
      setLoading(false);
    }
  }, [session, selectedGroup, db]);

  useEffect(() => {
    fetchSignups();
  }, [session.id, selectedGroup, refreshTrigger, fetchSignups]);

  useEffect(() => {
    // Listen for external refresh triggers
    fetchSignups();
  }, [onRefresh, fetchSignups]);

  const isSignedUp = () => {
    return user && signups.some(signup => signup.userId === user.uid);
  };

  const getUserSignupStatus = () => {
    if (!user) return null;
    return signups.find(signup => signup.userId === user.uid);
  };

  const confirmedSignups = signups.filter(signup => signup.status === 'confirmed');
  const waitlistSignups = signups.filter(signup => signup.status === 'waitlist');
  const userSignup = getUserSignupStatus();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          ← Back
        </button>
        <div>
          <h3 className="text-xl font-semibold text-gray-800">
            {new Date(session.date).toLocaleDateString()} at {session.time}
          </h3>
          {session.description && (
            <p className="text-gray-600">{session.description}</p>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex gap-4">
          <button
            onClick={() => onSignUp(session.id, session)}
            className={`px-4 py-2 rounded-lg ${isSignedUp()
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
          >
            {isSignedUp() ? 'Cancel Signup' : 'Sign Up'}
          </button>

          {userSignup && (
            <div className={`px-4 py-2 rounded-lg text-white ${userSignup.status === 'confirmed' ? 'bg-green-500' : 'bg-yellow-500'
              }`}>
              {userSignup.status === 'confirmed' ? 'Confirmed' : `Waitlist #${waitlistSignups.findIndex(s => s.userId === user.uid) + 1}`}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Confirmed Players */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">
            Confirmed Players ({confirmedSignups.length}/{session.maxPlayers})
          </h4>
          {loading ? (
            <div className="text-gray-500">Loading...</div>
          ) : confirmedSignups.length === 0 ? (
            <div className="text-gray-500">No confirmed players yet</div>
          ) : (
            <div className="space-y-2">
              {confirmedSignups.map((signup, index) => (
                <div key={signup.id} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                  <span className="text-green-600 font-medium">#{index + 1}</span>
                  <span>{signup.userName}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Waitlist */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">
            Waitlist ({waitlistSignups.length})
          </h4>
          {loading ? (
            <div className="text-gray-500">Loading...</div>
          ) : waitlistSignups.length === 0 ? (
            <div className="text-gray-500">No one on waitlist</div>
          ) : (
            <div className="space-y-2">
              {waitlistSignups.map((signup, index) => (
                <div key={signup.id} className="flex items-center gap-2 p-2 bg-yellow-50 rounded">
                  <span className="text-yellow-600 font-medium">#{index + 1}</span>
                  <span>{signup.userName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupsPage;
